import { FC, useState, useRef } from "react";
import Image from "next/image";
import ImageGalleryModalContent from "./ImageGalleryModalContent/ImageGalleryModalContent";
import { ImageGalleryProps } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

const ImageGallery: FC<ImageGalleryProps> = ({
  items,
  backgroundColor,
  noScroll,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const firstThumbnailRef = useRef<HTMLDivElement>(null);

  const thumbnailsPerRow = 3;

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const goNextImage = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goPrevImage = () => {
    setSelectedImageIndex((prevIndex) =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
  };

  const getThumbnailWidth = () => {
    if (firstThumbnailRef.current) {
      const style = window.getComputedStyle(firstThumbnailRef.current);
      const width = firstThumbnailRef.current.clientWidth;
      const marginRight = parseFloat(style.marginRight);
      return width + marginRight;
    }
    return 0;
  };

  const updateScrollButtons = () => {
    if (thumbnailsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        thumbnailsContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const slideNextThumbnails = () => {
    if (thumbnailsContainerRef.current) {
      const scrollAmount = getThumbnailWidth() * thumbnailsPerRow;
      thumbnailsContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
      setTimeout(updateScrollButtons, 500);
    }
  };

  const slidePrevThumbnails = () => {
    if (thumbnailsContainerRef.current) {
      const scrollAmount = getThumbnailWidth() * thumbnailsPerRow;
      thumbnailsContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
      setTimeout(updateScrollButtons, 500);
    }
  };

  return (
    <div style={{ backgroundColor: backgroundColor }}>
      {/* Image Row List */}
      <div
        className={`container mx-auto p-4 flex gap-4 ${
          noScroll ? "flex-wrap" : "overflow-x-auto"
        }`}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="cursor-pointer flex-shrink-0 w-40"
            onClick={() => handleImageClick(index)}
          >
            <div className="w-full h-40 overflow-hidden rounded-lg shadow-lg relative">
              <Image
                src={item.formats?.small?.url || item?.imageUrl}
                alt={item.imageAlt || "image"}
                fill
                sizes="(max-width: 768px) 100vw, 33vw"
                className="w-full h-full object-cover"
                quality={80}
              />
            </div>
            <p className="text-center mt-2 text-sm">{item.title}</p>
          </div>
        ))}
      </div>

      {/* Modal Gallery */}
      <ImageGalleryModalContent isOpen={isModalOpen} onClose={handleCloseModal}>
        <div className="relative w-full flex flex-col items-center gap-10">
          {/* Main Image Section */}
          <div className="w-full flex flex-col items-center">
            <div className="w-full h-96 overflow-hidden rounded-lg shadow-lg relative">
              <button
                className="absolute left-2 hidden lg:block top-1/2 transform -translate-y-1/2 bg-gray-200 w-10 h-10 p-2 rounded-full shadow-md hover:bg-gray-300 z-10"
                onClick={goPrevImage}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <Image
                src={
                  items[selectedImageIndex].formats?.large?.url ||
                  items[selectedImageIndex].imageUrl
                }
                alt={items[selectedImageIndex].imageAlt || "image"}
                fill
                sizes="(max-width: 1024px) 100vw, 66vw"
                className="w-full h-full object-contain"
                quality={80}
              />
              <button
                className="absolute right-4 hidden lg:block top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-200 p-2 rounded-full shadow-md hover:bg-gray-300 z-10"
                onClick={goNextImage}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
            <h2 className="mt-4 text-xl font-semibold">
              {items[selectedImageIndex].title}
            </h2>
            <p className="text-gray-500 mt-2">
              {items[selectedImageIndex].category}
            </p>
          </div>

          {/* Thumbnail Gallery Section with Scrolling */}
          <div className="w-full flex items-center justify-center relative lg:w-2/3">
            {/* Left Arrow */}
            {canScrollLeft && (
              <button
                className="absolute lg:-left-12 hidden lg:block top-1/2 transform -translate-y-1/2 bg-gray-200 w-10 h-10 p-2 rounded-full shadow-md hover:bg-gray-300 z-10"
                onClick={slidePrevThumbnails}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            )}

            {/* Thumbnails Container */}
            <div
              ref={thumbnailsContainerRef}
              className="flex gap-4 overflow-x-auto scrollbar-hide"
              onScroll={updateScrollButtons}
            >
              {items.map((item, index) => (
                <div
                  key={index}
                  ref={index === 0 ? firstThumbnailRef : null}
                  className={`cursor-pointer p-2 ${
                    selectedImageIndex === index
                      ? "border-2 border-blue-500"
                      : ""
                  }`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <div className="w-24 h-24 overflow-hidden rounded-lg relative">
                    <Image
                      src={item.formats?.small?.url || item?.imageUrl}
                      alt={item.imageAlt || "image"}
                      fill
                      sizes="96px"
                      className="w-full h-full object-cover"
                      quality={80}
                    />
                  </div>
                  {/* <p className="text-center mt-2 text-sm">{item.title}</p> */}
                </div>
              ))}
            </div>

            {/* Right Arrow */}
            {canScrollRight && (
              <button
                className="absolute -right-12 hidden lg:block top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gray-200 p-2 rounded-full shadow-md hover:bg-gray-300 z-10"
                onClick={slideNextThumbnails}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            )}
          </div>
        </div>
      </ImageGalleryModalContent>
    </div>
  );
};

export default ImageGallery;
